import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FileUploader} from 'ng2-file-upload';
import {environment} from "../../../../../environments/environment";
import {RequestService} from "../../../../services/request/request.service";
import {AuthService} from "../../../../services/auth/auth.service";
import {AlertBannerService, AlertType} from "../../../../lib/alert-banner";
import { ConstantsService } from 'src/app/services/constants/constants.service';
import { PermissionsService } from "../../../../services/permissions/permissions.service";

@Component({
  selector: 'app-bulk-upload',
  templateUrl: './bulk-upload.component.pug',
  styleUrls: ['./bulk-upload.component.scss'],
  standalone: false
})
export class BulkUploadComponent implements OnInit {
  @ViewChild('fileInput', {static: false}) fileInput: any;
  @Input() checkType = ConstantsService.EXIT_CHECK;
  @Output() tableData = new EventEmitter();
  @Output() errorMessages = new EventEmitter();
  @Output() clear = new EventEmitter();
  public recipientDetails: any;
  draftResponse = {};
  recipients: {}[] = [];
  recipientSequenceNumber = -1;
  uploader!: FileUploader;
  draftUid: string | null = '';
  draftPulseUid: string | null = '';
  fileIsOverDropZone = false;
  environmentUrl = environment.API;
  messages: Array<any> = [];
  maxFileSize = 4 * 1024 * 1024; // 4*1024*1024 = 4MB
  fileSizeError = false;
  file: any;
  currentFile: any;
  numberOfPeople = 0;
  importedData!: any;
  dataForTable = {};
  bulkErrorMap: any;
  loading = false;
  bulkUploadLoading = false;
  bulkUploadSuccessful = false;

  constructor(private requestService: RequestService,
    private authservice: AuthService,
    private alertList: AlertBannerService,
    private permissions: PermissionsService,
  ) {
    let checkUrl = '/check/exits/create';
    if(this.checkType == ConstantsService.PULSE_CHECK){
      checkUrl = '/check/pulses/create'
    }
    this.uploader = new FileUploader({
      url: this.environmentUrl + checkUrl,
      method: 'POST',
      disableMultipart: false,

      formatDataFunction: async (item) => {
        return new Promise((resolve, reject) => {
          resolve({
            name: item._file.name,
            length: item._file.size,
            contentType: item._file.type,
            date: new Date()
          });
        });
      },
    });

    this.uploader.response.subscribe(res => {
      if(this.checkType == ConstantsService.EXIT_CHECK){
        this.numberOfPeople = res['total_exits']
        localStorage.setItem('draft_uid', res['draft_uid']);
        this.draftUid = res['draft_uid'];
        this.retrieveRecipientTableData()
      } else if (this.checkType == ConstantsService.PULSE_CHECK){
        this.numberOfPeople = res['total_pulses']
        localStorage.setItem('draft_pulse_uid', res['draft_uid']);
        this.draftPulseUid = res['draft_uid'];
        this.retrievePulseRecipientTableData()
      }
    });
  }

  ngOnInit(): void {
    this.uploader.onAfterAddingFile = (file: any) => {
      this.fileInput.nativeElement.value = '';
      this.validateFile(file);
    };
    this.bulkErrorMap = new Map([
      ["more_than_permitted_rows", new Map([['exceeded_limit', $localize`The number of total recipients for `+ this.checkType +` surveys cannot exceed 5000. Please delete rows from your .csv file and try again.`]])],
      ["file", new Map([["invalid_type", $localize`Incorrect file type, please upload .csv only.`],
      ["wrong_headers", $localize`Oops, some of the content in your CSV does not match our CSV template. Please check your file and try again.`]])]
    ]);
    this.bulkUploadLoading = true;
    this.bulkUploadSuccessful = false;
  }

  createOrAppend(file) {
    this.loading = true;
    let payload = new FormData();
    if (this.file !== null) {
      payload.append('file', file);
    }

    // From last bug a few users can have pulse draft_uid in local storage
    // instead of asking them to logout and login let's take care of it by ourselves.

    if (localStorage.getItem('draft_pulse_uid') && this.checkType === ConstantsService.EXIT_CHECK) {
      localStorage.removeItem('draft_pulse_uid');
    }

    if (localStorage.getItem('draft_uid')) {
      this.draftUid = localStorage.getItem('draft_uid');
      this.addExitToDraft(payload);
    } else if(localStorage.getItem('draft_pulse_uid')){
      this.draftPulseUid = localStorage.getItem('draft_pulse_uid');
      this.addPulsesToDraft(payload)
    } else {
      if(this.checkType === ConstantsService.EXIT_CHECK){
        this.addRecipientDetails(payload);
      } else {
        this.addPulseRecipientDetails(payload);
      }
    }
    this.loading = false;
  }

  addRecipientDetails(payload) {
    this.requestService.createExit(payload).subscribe((data: any) => {
      this.draftUid = data['draft_uid'];
      this.recipientSequenceNumber = 1;
      if (typeof this.draftUid === "string") {
        localStorage.setItem('draft_uid', this.draftUid);
      }
      this.retrieveRecipientTableData();
    },
      (error: any) => {
        console.log(error);
        this.bulkUploadLoading = false;
        if (error.error['data'] !== null) {
          for (const [key, value] of Object.entries(error.error['data']['errors'])) {
            let key_map = this.bulkErrorMap.has(key) ? this.bulkErrorMap.get(key) : new Map();
            let message: string = key_map?.has(value) ? key_map?.get(value) : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
            this.messages.push({
              'type': 'danger',
              'message': message
            });
          }
        } else {
          this.messages.push({
            'type': 'danger',
            'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          });
        }
        this.errorMessages.emit(this.messages);
      })
  }

  addPulseRecipientDetails(payload) {
    this.requestService.createPulse(payload).subscribe((data: any) => {
      this.draftPulseUid = data['draft_uid'];
      this.recipientSequenceNumber = 1;
      if (typeof this.draftPulseUid === "string") {
        localStorage.setItem('draft_pulse_uid', this.draftPulseUid);
      }
      this.retrievePulseRecipientTableData();
    },
      (error: any) => {
        console.log(error);
        this.bulkUploadLoading = false;
        if (error.error['data'] !== null) {
          for (const [key, value] of Object.entries(error.error['data']['errors'])) {
            let key_map = this.bulkErrorMap.has(key) ? this.bulkErrorMap.get(key) : new Map();
            let message: string = key_map?.has(value) ? key_map?.get(value) : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
            this.messages.push({
              'type': 'danger',
              'message': message
            });
          }
        } else {
          this.messages.push({
            'type': 'danger',
            'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          });
        }
        this.errorMessages.emit(this.messages);
      })
  }

  addExitToDraft(payload) {
    this.requestService.appendExit(payload, this.draftUid).subscribe((data: any) => {
      this.numberOfPeople = data['rows_appended'];
      this.recipientSequenceNumber = data['sequence']
      this.dataForTable = {
        "buttonStatus": false,
        "numberOfPeople": this.numberOfPeople,
        "total": data['total_exits']
      };
      this.bulkUploadLoading = false;
      this.bulkUploadSuccessful = true;
      this.tableData.emit(this.dataForTable);
    },
      (error: any) => {
        console.log(error)
        this.bulkUploadLoading = false;
        this.bulkUploadSuccessful = false;
        for (const [key, value] of Object.entries(error.error['data']['errors'])) {
          let key_map = this.bulkErrorMap.has(key) ? this.bulkErrorMap.get(key) : new Map();
          let message: string = key_map?.has(value) ? key_map?.get(value) : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          this.messages.push({
            'type': 'danger',
            'message': message
          });
        }
        this.errorMessages.emit(this.messages);
      })
  }

  addPulsesToDraft(payload){
    this.requestService.appendPulse(payload, this.draftPulseUid).subscribe((data: any) => {
      this.numberOfPeople = data['rows_appended'];
      this.recipientSequenceNumber = data['sequence']
      this.dataForTable = {
        "buttonStatus": false,
        "numberOfPeople": this.numberOfPeople,
        "total": data['total_pulses']
      };
      this.bulkUploadLoading = false;
      this.bulkUploadSuccessful = true;
      this.tableData.emit(this.dataForTable);
    },
      (error: any) => {
        console.log("error", error)
        this.bulkUploadLoading = false;
        this.bulkUploadSuccessful = false;
        for (const [key, value] of Object.entries(error.error['data']['errors'])) {
          let key_map = this.bulkErrorMap.has(key) ? this.bulkErrorMap.get(key) : new Map();
          let message: string = key_map?.has(value) ? key_map?.get(value) : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          this.messages.push({
            'type': 'danger',
            'message': message
          });
        }
        this.errorMessages.emit(this.messages);
      })
  }

  retrieveRecipientTableData() {
    this.requestService.retrieveDraftExit(this.draftUid).subscribe((data: any) => {
      this.importedData = data['exits'];
      this.numberOfPeople = data['total_exits'];
      this.dataForTable = {
        "buttonStatus": false,
        "numberOfPeople": this.numberOfPeople,
        "total": data['total_exits']
      };
      this.tableData.emit(this.dataForTable);
      this.bulkUploadLoading = false;
      this.bulkUploadSuccessful = true;
      this.tableData.emit(this.dataForTable);
    },
      (err: any) => {
        this.bulkUploadLoading = false;
        this.bulkUploadSuccessful = false;
        if (err.status === 404) {
          localStorage.removeItem('draft_uid')
        } else {
          console.log(err);
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      })
  }

  retrievePulseRecipientTableData() {
    this.requestService.retrieveDraftPulse(this.draftPulseUid).subscribe((data: any) => {
      this.importedData = data['pulses'];
      this.numberOfPeople = data['total_pulses'];
      this.dataForTable = {
        "buttonStatus": false,
        "numberOfPeople": this.numberOfPeople,
        "total": data['total_pulses']
      };
      this.tableData.emit(this.dataForTable);
      this.bulkUploadLoading = false;
      this.bulkUploadSuccessful = true;
    },
      (err: any) => {
        this.bulkUploadLoading = false;
        this.bulkUploadSuccessful = false;

        if (err.status === 404) {
          localStorage.removeItem('draft_pulse_uid')
        } else {
          console.log(err);
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      })
  }

  // Validate selected image file before adding to queue and preview
  validateFile(file: any) {
    // Reset errors
    this.fileSizeError = false;
    if (file.some.size > this.maxFileSize) {
      // File size too large
      this.fileSizeError = true;
      this.bulkUploadLoading = false;
      this.bulkUploadSuccessful = false;
    }
    this.currentFile = file;

    // Only add file to queue if no validation errors
    if (!this.fileSizeError) {
      this.uploader.queue = [file];
    }
  }

  afterFileDropped(file) {
    this.createOrAppend(file[0])
  }

  afterFileSelected(event) {
    this.createOrAppend(this.uploader.queue[0].file.rawFile)
  }

  fileOverBase(event) {
    this.fileIsOverDropZone = event;
  }

  clearFile() {
    if(this.recipientSequenceNumber > -1) {
      if(this.checkType == ConstantsService.PULSE_CHECK) {
        this.requestService.removePulse(this.draftPulseUid, -1, this.recipientSequenceNumber).subscribe(res => {
          this.recipientSequenceNumber = -1
        })
      } else if(this.checkType == ConstantsService.EXIT_CHECK) {
        this.requestService.removeExit(this.draftUid, -1, this.recipientSequenceNumber).subscribe(res => {
          this.recipientSequenceNumber = -1
        })
      }
    }

    this.currentFile = null;
    this.importedData = {};
    this.uploader.queue.pop();
    this.dataForTable = {
      "buttonStatus": true,
      "numberOfPeople": 0
    }
    this.messages = [];
    this.errorMessages.emit(this.messages);
    this.tableData.emit(this.dataForTable);
    this.bulkUploadLoading = true;
    this.bulkUploadSuccessful = false;
  }

}
